<script setup lang="ts">
  import type { IDestinationItem, IOriginItem } from '@/components/clp/types'
  import type { INearestAirport } from '@/types/OrigDest'
  import { DEFAULT_ORIGIN_CODE } from '@/utils/constants/origDest'
  import {
    SORT_DEFAULT,
    SORT_ASC_PRICE,
    SORT_DESC_PRICE,
    SORT_ASC,
    SORT_DESC,
    SORT_ASC_COUNTRY,
    SORT_DESC_COUNTRY,
    SORT_OPTION_LIST
  } from '@/components/clp/constants'
  import useCLPDataLayer from '@/composables/data-layers/useCLPDataLayer'

  const props = defineProps<{
    destinations: IDestinationItem[]
    originList: IOriginItem[]
  }>()

  const route = useRoute()
  const nearestAirportCookie = useCookie<INearestAirport>('nearestAirportData')

  function getDefaultOrigin() {
    const query = route.query?.origin as string

    if (query) {
      const origin = props.originList.find((item) => item.code === query)

      if (origin?.city) {
        return origin
      }
    }

    if (nearestAirportCookie.value) {
      const origin = props.originList.find((item) => {
        return item.code === nearestAirportCookie.value?.code
      })

      if (origin?.city) {
        return origin
      }
    }

    return props.originList.find((item) => {
      return item.code === DEFAULT_ORIGIN_CODE
    })
  }

  const selectedOrigin = useState<IOriginItem | null>('selectedOrigin', () => null)

  const destinationKeys = ['destinApt', 'destinCity', 'destinCode', 'destinCountry'] as const
  const isFilteredDestin = (item: IDestinationItem, searchLower: string) => {
    if (!searchLower.length) {
      return true
    }

    return destinationKeys.some((key) => {
      const value = item[key]
      if (!value) {
        return false
      }

      return value.toLowerCase().startsWith(searchLower)
    })
  }

  const sortingFunctions = {
    [SORT_ASC]: (a: IDestinationItem, b: IDestinationItem) => a.destinCity?.localeCompare(b?.destinCity),
    [SORT_DESC]: (a: IDestinationItem, b: IDestinationItem) => b.destinCity?.localeCompare(a?.destinCity),
    [SORT_ASC_PRICE]: (a: IDestinationItem, b: IDestinationItem) => a?.priceOW - b?.priceOW,
    [SORT_DESC_PRICE]: (a: IDestinationItem, b: IDestinationItem) => b?.priceOW - a?.priceOW,
    [SORT_ASC_COUNTRY]: (a: IDestinationItem, b: IDestinationItem) => a?.destinCountry?.localeCompare(b?.destinCountry),
    [SORT_DESC_COUNTRY]: (a: IDestinationItem, b: IDestinationItem) =>
      b?.destinCountry?.localeCompare(a?.destinCountry),
    [SORT_DEFAULT]: () => 0
  } as const

  type SORTING_METHODS = keyof typeof sortingFunctions

  const searchDestination = ref('')
  const directFlightsOnly = ref(false)

  const filteredDestins = computed(() => {
    const selectedOriginCode = selectedOrigin.value?.code
    const searchLower = searchDestination.value.toLowerCase()
    const directOnly = directFlightsOnly.value

    return props.destinations.filter((item: IDestinationItem) => {
      if (directOnly && !item.hasBTDirect) {
        return false
      }

      if (item.originCode !== selectedOriginCode) {
        return false
      }

      return searchDestination.value ? isFilteredDestin(item, searchLower) : true
    })
  })

  const selectedSorter = ref<SORTING_METHODS>(SORT_DEFAULT)

  const isDefaultFilterState = computed(
    () => !searchDestination.value && !directFlightsOnly.value && selectedSorter.value === SORT_DEFAULT
  )

  const { isDesktop } = useBTBreakpoints()
  const { pushCLPWithOriginDataLayer } = useCLPDataLayer()
  const emit = defineEmits({
    'update:selected-origin': (value: IOriginItem | null) => value,
    'update:sorted-items': (value: IDestinationItem[]) => value,
    'is-default-filters': (value: boolean) => value
  })

  function onDirectFlightsOnlyChange(newVal?: boolean) {
    if (!newVal) {
      return
    }

    pushCLPWithOriginDataLayer({
      generic_event_name: 'filtering',
      event_category: 'direct_flight_checkbox'
    })
  }

  function onSortByChange(sorting?: SORTING_METHODS) {
    if (!sorting) {
      return
    }

    pushCLPWithOriginDataLayer({
      generic_event_name: 'filtering',
      sort_by: sorting,
      event_category: 'sort_by'
    })

    emit('update:sorted-items', sortItems(sorting))
  }

  function sortItems(sorting: SORTING_METHODS) {
    const items = filteredDestins.value.slice()

    try {
      items.sort(sortingFunctions[sorting])
    } catch (error) {
      selectedSorter.value = SORT_DEFAULT
      console.error(error)
    }

    return items
  }

  const onSearchQueryChange = useDebounceFn((newVal?: string) => {
    searchDestination.value = newVal || ''

    if (newVal) {
      pushCLPWithOriginDataLayer({
        generic_event_name: 'filtering',
        event_category: 'search',
        event_label: newVal
      })
    }
  }, 300)

  emit('update:sorted-items', sortItems(selectedSorter.value))

  watch(
    filteredDestins,
    () => {
      emit('update:sorted-items', sortItems(selectedSorter.value))
    },
    {
      deep: true
    }
  )

  watch(
    isDefaultFilterState,
    (newVal) => {
      emit('is-default-filters', newVal)
    },
    { immediate: true }
  )

  watch(
    () => props.originList,
    (newList) => {
      if (!selectedOrigin.value && newList.length) {
        selectedOrigin.value = getDefaultOrigin() ?? null
      }
    },
    { immediate: true }
  )

  watch(selectedOrigin, (newVal) => {
    emit('update:selected-origin', newVal)
  })
</script>
<template>
  <div class="mb-8 rounded-sm bg-grey px-5 py-6 md:p-8">
    <div class="flex flex-col md:flex-row">
      <p class="mb-1 mr-2 text-bt-blue-dark text-h5 md:mb-0 md:mr-4">{{ $t('clp.flightsFrom') }}</p>
      <ClpSearchDropdown
        v-model="selectedOrigin"
        :data="originList"
        class="md:mr-10"
      />
      <UiCheckbox
        v-if="isDesktop"
        id="direct-flights-only-desktop"
        v-model="directFlightsOnly"
        :label="$t('origDest.directFlightsOnly')"
        @update:model-value="(val) => onDirectFlightsOnlyChange(!!val)"
      />
    </div>
    <div class="mt-6 flex flex-col gap-4 md:flex-row">
      <UiInputBase
        input-key="search"
        name="filter"
        path=""
        :placeholder="$t('clp.searchPlaceholder')"
        class="w-full"
        :label="$t('clp.searchPlaceholder')"
        :sr-only-label="true"
        @update:model-value="onSearchQueryChange"
      />
      <UiSelect
        v-if="selectedSorter"
        v-model="selectedSorter"
        :items="SORT_OPTION_LIST"
        label="clp.sorterBlock.label"
        sr-only-label
        class="w-full"
        name="selectedSorter"
        @update:model-value="(val) => onSortByChange(val as SORTING_METHODS)"
      />
    </div>
    <UiCheckbox
      v-if="!isDesktop"
      id="direct-flights-only-mobile"
      v-model="directFlightsOnly"
      :label="$t('origDest.directFlightsOnly')"
      class="mt-6"
    />
  </div>
</template>
